import imageCompression from 'browser-image-compression';

export interface CompressedFile {
  originalFile: File;
  compressedFile: File | Blob;
  compressionRatio: number;
  originalSize: number;
  compressedSize: number;
}

// Intelligente Bildkomprimierung basierend auf Dateigröße und Typ
const compressImage = async (file: File): Promise<File> => {
  const fileSizeMB = file.size / (1024 * 1024);
  
  // Intelligente Einstellungen basierend auf Dateigröße
  let options;
  
  if (fileSizeMB > 10) {
    // Sehr große Dateien (> 10MB) - stärkere Komprimierung
    options = {
      maxSizeMB: 2.0,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type,
      initialQuality: 0.82,
      alwaysKeepResolution: false
    };
  } else if (fileSizeMB > 5) {
    // Große Dateien (5-10MB) - moderate Komprimierung
    options = {
      maxSizeMB: 2.5,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      fileType: file.type,
      initialQuality: 0.85,
      alwaysKeepResolution: false
    };
  } else if (fileSizeMB > 2) {
    // Mittlere Dateien (2-5MB) - sanfte Komprimierung
    options = {
      maxSizeMB: 1.8,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      fileType: file.type,
      initialQuality: 0.88,
      alwaysKeepResolution: true // Auflösung beibehalten für Dokumente
    };
  } else {
    // Kleine Dateien (< 2MB) - minimale Komprimierung für beste Qualität
    options = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 2560, // Höhere Auflösung für kleine Dateien
      useWebWorker: true,
      fileType: file.type,
      initialQuality: 0.92, // Sehr hohe Qualität
      alwaysKeepResolution: true
    };
  }

  try {
    const compressedFile = await imageCompression(file, options);
    
    // Wenn die Komprimierung die Datei größer macht, Original behalten
    if (compressedFile.size >= file.size * 0.95) {
      console.log('Komprimierung nicht effektiv, verwende Originaldatei');
      return file;
    }
    
    return compressedFile;
  } catch (error) {
    console.warn('Bildkomprimierung fehlgeschlagen, verwende Originaldatei:', error);
    return file;
  }
};

// Komprimiert PDFs (vereinfacht - reduziert nur Metadaten)
const compressPDF = async (file: File): Promise<File> => {
  // Für PDFs ist eine echte Komprimierung komplexer
  // Hier könnten Sie eine PDF-Bibliothek wie pdf-lib verwenden
  // Vorerst geben wir die Originaldatei zurück
  return file;
};

// Komprimiert Text-Dateien
const compressText = async (file: File): Promise<Blob> => {
  const text = await file.text();
  
  // Entfernen Sie überflüssige Leerzeichen und Zeilenumbrüche
  const compressedText = text
    .replace(/\s+/g, ' ') // Mehrfache Leerzeichen durch eines ersetzen
    .replace(/\n\s*\n/g, '\n') // Mehrfache Zeilenumbrüche reduzieren
    .trim();

  return new Blob([compressedText], { type: file.type });
};

// Haupt-Komprimierungsfunktion
export const compressFile = async (file: File): Promise<CompressedFile> => {
  const originalSize = file.size;
  let compressedFile: File | Blob = file;

  try {
    if (file.type.startsWith('image/')) {
      compressedFile = await compressImage(file);
    } else if (file.type === 'application/pdf') {
      compressedFile = await compressPDF(file);
    } else if (file.type.startsWith('text/') || file.type === 'application/json') {
      compressedFile = await compressText(file);
    }
    // Für andere Dateitypen keine Komprimierung

    const compressedSize = compressedFile.size;
    const compressionRatio = originalSize > 0 ? (1 - compressedSize / originalSize) * 100 : 0;

    return {
      originalFile: file,
      compressedFile,
      compressionRatio,
      originalSize,
      compressedSize
    };
  } catch (error) {
    console.error('Komprimierung fehlgeschlagen:', error);
    return {
      originalFile: file,
      compressedFile: file,
      compressionRatio: 0,
      originalSize,
      compressedSize: originalSize
    };
  }
};

// Formatiert Dateigröße in lesbarem Format
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};